import React, { useState } from 'react'

import { ReactComponent as PaperPlaneTilt } from '@app/assets/Small/Outline/PaperPlaneTilt.svg'
import { FormattedError } from '@app/common/Components/FormattedError'
import {
  AlertType,
  useNotifications
} from '@app/common/services/Notifications/NotificationsProvider'
import { useIntl } from '@app/intl/Intl'
import { bemJoin } from '@app/lib/bem'
import { Button } from '@app/lib/inputs/Button/Button'
import { UploadIconButton } from '@app/lib/inputs/UploadIconButton/uploadIconButton'

import { DocumentLabel } from '../DocumentLabel'

import './index.scss'

type MessageBoxProps = {
  placeholder?: string
  files?: File[]
  onSubmit: (message: string) => Promise<void> | void
  onFileUpload: () => void
  onFileRemove?: (fileIndex: number) => void
  disabled?: boolean
}

export const MessageBox = ({
  placeholder,
  files,
  onSubmit,
  onFileUpload,
  onFileRemove,
  disabled = false
}: MessageBoxProps) => {
  const { pushNotification } = useNotifications()
  const [message, setMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const b = bemJoin('message-box')
  const { formatMessage } = useIntl()

  if (!placeholder) {
    placeholder = formatMessage({ id: 'MessageBox.Textarea.Placeholder-Lawyer' })
  }

  const handleSubmit = async () => {
    if (message.trim() && onSubmit && !isSubmitting) {
      setIsSubmitting(true)

      try {
        await onSubmit(message.trim())
        setMessage('')
      } catch (err) {
        pushNotification({
          type: AlertType.error,
          message: <FormattedError error={err} />
        })
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  const handleFileRemove = (index: number) => {
    if (onFileRemove && !disabled && !isSubmitting) {
      onFileRemove(index)
    }
  }

  const hasText = message.trim().length > 0
  const disableEdit = disabled || isSubmitting
  const disableSubmit = disableEdit || !hasText

  return (
    <div className={b()}>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value || '')}
        placeholder={placeholder}
        rows={6}
        className={b('input')}
        disabled={disableEdit}
      />
      <div className={b('labels')}>
        {files &&
          files.map((file, index) => (
            <DocumentLabel
              key={`${file.name}-${index}`}
              filename={file.name}
              onRemove={() => handleFileRemove(index)}
            />
          ))}
      </div>
      <div className={b('buttons-container')}>
        <UploadIconButton onFileUpload={onFileUpload} disabled={disableEdit} />
        <Button
          className={b('action-button')}
          leftIcon={<PaperPlaneTilt />}
          disabled={disableSubmit}
          onClick={handleSubmit}
          variant='solid'
          color='secondary'
        >
          {formatMessage({ id: 'MessageBox.Submit.Text' })}
        </Button>
      </div>
    </div>
  )
}
